import React, { useState } from "react";
import { X, Sparkles, ExternalLink, Bookmark, Loader2, Check, Copy, FileDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function buildScript(story, ai) {
  const lines = [];
  lines.push(`# ${story.title}`);
  lines.push(`Source: ${story.source} — ${story.url}`);
  lines.push("");
  if (ai.hooks?.length) {
    lines.push("## OPENING HOOK");
    lines.push(ai.hooks[0]);
    lines.push("");
  }
  if (ai.talking_points?.length) {
    lines.push("## TALKING POINTS");
    ai.talking_points.forEach((t, i) => lines.push(`${i + 1}. ${t}`));
    lines.push("");
  }
  if (ai.topics?.length) {
    lines.push("## RELATED TOPICS TO BRIDGE TO");
    ai.topics.forEach((t) => lines.push(`- ${t}`));
    lines.push("");
  }
  if (ai.titles?.length) {
    lines.push("## TITLE OPTIONS");
    ai.titles.forEach((t) => lines.push(`- ${t}`));
  }
  return lines.join("\n");
}

export default function StoryDetail({ story, onClose }) {
  const { refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setAi(null);
    try {
      const { data } = await api.post("/generate", { story });
      setAi(data);
      // Refresh /auth/me so the sidebar generation counter updates immediately
      // without requiring the user to reload the page. Fire-and-forget — the
      // AI result is already rendered, so any delay here shouldn't block UX.
      refreshProfile().catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI failed");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!ai) return;
    try {
      await api.post("/saved", { story, ...ai });
      setSaved(true);
      toast.success("Saved to your library");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copyScript = async () => {
    if (!ai) return;
    try {
      await navigator.clipboard.writeText(buildScript(story, ai));
      setCopied(true);
      toast.success("Script copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy. Check browser permissions.");
    }
  };

  const downloadPdf = async () => {
    if (!ai) return;
    setDownloading(true);
    try {
      const res = await api.post("/packet/pdf", { story, ...ai }, { responseType: "blob" });
      const slug = story.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "story-packet";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fount-${slug}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded — ready to print or share");
    } catch {
      toast.error("Couldn't create the PDF. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (!story) return null;

  return (
    <div
      data-testid="story-detail-overlay"
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
    >
      <div
        className="bg-white w-full lg:max-w-2xl h-full overflow-y-auto border-l border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between z-10">
          <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-gray-500 truncate pr-3">
            Story · {story.category}
          </div>
          <button
            data-testid="close-detail-button"
            onClick={onClose}
            className="text-gray-500 hover:text-[#0a0a0a] -mr-2 p-2"
            aria-label="Close"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-4 sm:px-8 py-6 sm:py-8">
          <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight tracking-tight">
            {story.title}
          </h1>
          <div className="flex items-center gap-3 mt-3 text-[10px] sm:text-xs font-mono text-gray-500 uppercase tracking-widest flex-wrap">
            <span>{story.source}</span>
            <span>·</span>
            <a
              href={story.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[#7C3AED] hover:underline"
            >
              Source <ExternalLink size={12} strokeWidth={1.5} />
            </a>
          </div>

          {story.summary && (
            <p className="text-[15px] sm:text-base text-gray-700 leading-relaxed mt-5 sm:mt-6">
              {story.summary}
            </p>
          )}

          <div className="mt-6 sm:mt-8">
            <ActionBar
              loading={loading}
              hasResult={!!ai}
              saved={saved}
              copied={copied}
              downloading={downloading}
              onGenerate={generate}
              onSave={save}
              onCopy={copyScript}
              onDownloadPdf={downloadPdf}
            />
          </div>

          {ai && (
            <div className="mt-8 sm:mt-10 space-y-8 sm:space-y-10" data-testid="ai-output">
              <Section title="Talking Points" items={ai.talking_points} />
              <Section title="Topic Ideas" items={ai.topics} />
              <Section title="Opening Hooks" items={ai.hooks} kind="hook" />
              <Section title="Episode / Video Titles" items={ai.titles} kind="title" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionBar({ loading, hasResult, saved, copied, downloading, onGenerate, onSave, onCopy, onDownloadPdf }) {
  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
      <button
        data-testid="generate-button"
        onClick={onGenerate}
        disabled={loading}
        className="inline-flex items-center justify-center gap-2 bg-brand-gradient text-white hover:opacity-95 disabled:opacity-60 px-6 py-3 font-medium transition-opacity rounded-lg"
      >
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Sparkles size={16} strokeWidth={1.5} />
        )}
        {loading ? "Generating…" : hasResult ? "Regenerate" : "Generate talking points"}
      </button>
      {hasResult && (
        <button
          data-testid="save-button"
          onClick={onSave}
          disabled={saved}
          className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 disabled:opacity-60 px-6 py-3 font-medium transition-colors rounded-lg"
        >
          {saved ? <Check size={16} strokeWidth={1.5} /> : <Bookmark size={16} strokeWidth={1.5} />}
          {saved ? "Saved" : "Save to library"}
        </button>
      )}
      {hasResult && (
        <button
          data-testid="copy-script-button"
          onClick={onCopy}
          className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 px-6 py-3 font-medium transition-colors rounded-lg"
        >
          {copied ? <Check size={16} strokeWidth={1.5} /> : <Copy size={16} strokeWidth={1.5} />}
          {copied ? "Copied" : "Copy as script"}
        </button>
      )}
      {hasResult && (
        <button
          data-testid="download-pdf-button"
          onClick={onDownloadPdf}
          disabled={downloading}
          className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 disabled:opacity-60 px-6 py-3 font-medium transition-colors rounded-lg"
        >
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} strokeWidth={1.5} />}
          {downloading ? "Building PDF…" : "Download PDF"}
        </button>
      )}
    </div>
  );
}

function Section({ title, items, kind }) {
  if (!items || items.length === 0) return null;
  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500">
          {title}
        </h3>
        <div className="flex-1 h-px bg-gray-200" />
        <span className="font-mono text-xs text-gray-400">{items.length}</span>
      </div>
      <ol className="space-y-3">
        {items.map((it, i) => (
          <li
            key={`${it}-${i}`}
            className="border border-gray-200 p-4 hover:border-[#0a0a0a] transition-colors"
          >
            <div className="flex gap-4">
              <span className="font-mono text-xs text-gray-400 pt-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p
                className={
                  kind === "title"
                    ? "font-serif text-xl leading-snug"
                    : "text-[15px] leading-relaxed text-gray-800"
                }
              >
                {it}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
